/**
 * Interprete determinista sin conexion para expresiones financieras en español
 * (FR-VOICE-2, FR-VOICE-5, FR-VOICE-8, spec/02-architecture.md §3).
 *
 * Convierte expresiones habladas o escritas en propuestas estructuradas de asientos
 * de doble entrada (INV-1, INV-2). No persiste nada: la propuesta es editable campo
 * a campo y requiere confirmacion explicita del usuario (FR-VOICE-3, FR-VOICE-4).
 */

import type { Account } from "../core/account.js";

export interface ParsedProposal {
  readonly date: string; // YYYY-MM-DD
  readonly description: string;
  readonly amountMinor: bigint | null;
  readonly currency: string;
  readonly scale: number;
  readonly debitAccountId: string | null;
  readonly creditAccountId: string | null;
  readonly missingFields: readonly ("amount" | "description" | "debitAccount" | "creditAccount")[];
  readonly rawText: string;
}

const SPANISH_NUMBER_WORDS: Record<string, number> = {
  un: 1,
  uno: 1,
  una: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
  veinte: 20,
  treinta: 30,
  cuarenta: 40,
  cincuenta: 50,
  cien: 100,
};

function getTodayIsoDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getYesterdayIsoDate(): string {
  const now = new Date();
  now.setDate(now.getDate() - 1);
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseAmount(text: string): { amountMinor: bigint; scale: number } | null {
  // Regex para importes numericos: 12, 12.50, 12,50, 1.200,50
  const match = text.match(/\b(\d+(?:[.,]\d{1,2})?)\b/);
  if (match && match[1]) {
    const raw = match[1].replace(",", ".");
    const num = parseFloat(raw);
    if (!isNaN(num) && num > 0) {
      const minor = BigInt(Math.round(num * 100));
      return { amountMinor: minor, scale: 2 };
    }
  }

  // Comprobar palabras numericas sencillas
  for (const [word, val] of Object.entries(SPANISH_NUMBER_WORDS)) {
    const wordRegex = new RegExp(`\\b${word}\\b`, "i");
    if (wordRegex.test(text)) {
      return { amountMinor: BigInt(val * 100), scale: 2 };
    }
  }

  return null;
}

export function parseDate(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("ayer")) {
    return getYesterdayIsoDate();
  }
  // Fecha explicita YYYY-MM-DD
  const isoMatch = lower.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoMatch && isoMatch[1]) return isoMatch[1];

  return getTodayIsoDate();
}

/**
 * Mapeo heuristico de texto a cuentas conocidas en base a palabras clave.
 */
export function matchAccount(
  text: string,
  accounts: readonly Account[],
  preferredTypes: readonly Account["type"][],
): Account | null {
  const lower = text.toLowerCase();

  // Filtrar cuentas por tipo preferido
  const candidateAccounts = accounts.filter((a) => !a.archived && preferredTypes.includes(a.type));

  // 1. Coincidencia por palabras clave directas
  const keywords: Record<string, string[]> = {
    "acc-card": ["tarjeta", "visa", "mastercard", "crédito", "credito"],
    "acc-bank": ["banco", "cuenta", "transferencia", "cuenta bancaria"],
    "acc-cash": ["efectivo", "metálico", "metalico", "cajero", "monedas"],
    "acc-salary": ["nómina", "nomina", "sueldo", "salario"],
    "acc-dining": ["café", "cafe", "restaurante", "bar", "cena", "comida", "almuerzo", "desayuno", "ocio", "cerveza"],
    "acc-groceries": ["supermercado", "super", "comestibles", "alimentación", "alimentacion", "compra"],
    "acc-housing": ["alquiler", "hipoteca", "comunidad", "piso", "casa"],
    "acc-transport": ["transporte", "metro", "bus", "autobús", "gasolina", "taxi", "uber"],
    "acc-utilities": ["luz", "agua", "gas", "internet", "teléfono", "telefono"],
  };

  for (const [accId, terms] of Object.entries(keywords)) {
    if (terms.some((term) => lower.includes(term))) {
      const found = candidateAccounts.find((a) => a.accountId === accId);
      if (found) return found;
    }
  }

  // 2. Coincidencia por nombre de la cuenta
  for (const acc of candidateAccounts) {
    const accNameLower = acc.name.toLowerCase();
    if (lower.includes(accNameLower)) {
      return acc;
    }
  }

  // 3. Fallback al primer candidato del tipo preferido
  return candidateAccounts[0] ?? null;
}

/**
 * Parsea una expresion en español y genera una propuesta de asiento (FR-VOICE-2).
 */
export function parseFinancialExpression(
  text: string,
  accounts: readonly Account[],
  defaultCurrency = "PEN",
): ParsedProposal {
  const cleanText = text.trim();
  const lower = cleanText.toLowerCase();

  let detectedCurrency = defaultCurrency;
  if (/(?:^|\s|[.,])(soles|sol|pen)(?:$|\s|[.,])/i.test(lower)) {
    detectedCurrency = "PEN";
  } else if (/(?:^|\s|[.,])(dólares|dolares|dólar|dolar|usd)(?:$|\s|[.,])/i.test(lower)) {
    detectedCurrency = "USD";
  } else if (/(?:^|\s|[.,])(euros|euro|eur)(?:$|\s|[.,])/i.test(lower)) {
    detectedCurrency = "EUR";
  }

  const amountData = parseAmount(lower);
  const date = parseDate(lower);

  // Determinar intencion: gasto, ingreso o transferencia (compatible con caracteres acentuados)
  const isIncome = /(?:^|\s|[.,])(ingreso|nómina|nomina|cobré|cobre|recibí|recibi|gané|gane)(?:$|\s|[.,])/i.test(lower);
  const isTransfer = /(?:^|\s|[.,])(transferí|transferi|traspasé|traspase|moví|movi|transferencia)(?:$|\s|[.,])/i.test(lower);

  let debitAccountId: string | null = null;
  let creditAccountId: string | null = null;
  let description = cleanText;

  if (isIncome) {
    // Ingreso: Debe = Activo (banco/efectivo), Haber = Ingreso (nómina/otros)
    const assetAccount = matchAccount(lower, accounts, ["asset"]);
    const incomeAccount = matchAccount(lower, accounts, ["income"]);
    debitAccountId = assetAccount ? assetAccount.accountId : null;
    creditAccountId = incomeAccount ? incomeAccount.accountId : null;

    // Extraer descripcion resumida
    const conceptMatch = lower.match(/(?:por|de|en concepto de)\s+([^.,]+)/);
    description = conceptMatch && conceptMatch[1] ? conceptMatch[1].trim() : "Ingreso";
  } else if (isTransfer) {
    // Transferencia: Debe = Activo destino, Haber = Activo origen
    const assetAccounts = accounts.filter((a) => a.type === "asset" && !a.archived);
    // Extraer origen ("de X") y destino ("a Y")
    const fromMatch = lower.match(/(?:de|desde)\s+([a-záéíóúñ]+)/i);
    const toMatch = lower.match(/(?:a|hacia)\s+([a-záéíóúñ]+)/i);

    const fromAcc = fromMatch && fromMatch[1] ? matchAccount(fromMatch[1], assetAccounts, ["asset"]) : null;
    const toAcc = toMatch && toMatch[1] ? matchAccount(toMatch[1], assetAccounts, ["asset"]) : null;

    creditAccountId = fromAcc ? fromAcc.accountId : (assetAccounts[0]?.accountId ?? null);
    debitAccountId = toAcc ? toAcc.accountId : (assetAccounts[1]?.accountId ?? null);
    description = "Transferencia interna";
  } else {
    // Gasto habitual: Debe = Gasto, Haber = Activo o Pasivo (efectivo, tarjeta, banco)
    const expenseAccount = matchAccount(lower, accounts, ["expense"]);
    const paymentAccount = matchAccount(lower, accounts, ["asset", "liability"]);

    debitAccountId = expenseAccount ? expenseAccount.accountId : null;
    creditAccountId = paymentAccount ? paymentAccount.accountId : null;

    // Extraer concepto ("en café", "en supermercado", "en taxi")
    const conceptMatch = lower.match(/(?:en|para)\s+([^.,\s]+(?:\s+[^.,\s]+)?)/);
    if (conceptMatch && conceptMatch[1]) {
      description = conceptMatch[1].replace(/\b(hoy|ayer|con|la|el|tarjeta|banco)\b/gi, "").trim();
      if (!description) description = "Gasto";
    } else {
      description = "Gasto general";
    }
  }

  // Determinar campos faltantes (FR-VOICE-5)
  const missingFields: ("amount" | "description" | "debitAccount" | "creditAccount")[] = [];
  if (!amountData) missingFields.push("amount");
  if (!description) missingFields.push("description");
  if (!debitAccountId) missingFields.push("debitAccount");
  if (!creditAccountId) missingFields.push("creditAccount");

  return {
    date,
    description: description.charAt(0).toUpperCase() + description.slice(1),
    amountMinor: amountData ? amountData.amountMinor : null,
    currency: detectedCurrency,
    scale: amountData ? amountData.scale : 2,
    debitAccountId,
    creditAccountId,
    missingFields,
    rawText: cleanText,
  };
}
